/**
 * 数据概览仪表板
 */

import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Alert, Spin, Typography, Tag, Progress } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { StatsOverview, WarningStats, BatchInfo } from '../../types';
import { getStatsOverview, getWarningStats, getInspectionBatches, getBatchReport } from '../../services/api';
import { formatNumber, formatDate, getCityLevelLabel } from '../../utils/helpers';
import { CHART_COLORS } from '../../utils/constants';
import { StatusCalculationEngine, StatusEnum } from '../../services/StatusCalculationEngine';

const { Text } = Typography;

// 解析参考范围字符串
const parseReferenceRange = (referenceRange: string): { min?: number; max?: number } => {
  if (!referenceRange) return {};

  // 处理 "50-100" 格式
  const rangeMatch = referenceRange.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // 处理 "≤100" 或 "<=100" 格式
  const maxMatch = referenceRange.match(/[≤<=](\d+(?:\.\d+)?)/);
  if (maxMatch) {
    return { max: parseFloat(maxMatch[1]) };
  }

  // 处理 "≥50" 或 ">=50" 格式
  const minMatch = referenceRange.match(/[≥>=](\d+(?:\.\d+)?)/);
  if (minMatch) {
    return { min: parseFloat(minMatch[1]) };
  }

  return {};
};

// 计算项目状态
const calculateItemStatus = (item: any) => {
  const { min, max } = parseReferenceRange(item.reference_range);

  try {
    const result = StatusCalculationEngine.calculate({
      actualValue: item.actual_value,
      referenceMin: min,
      referenceMax: max,
      elementName: item.element_name,
      cityLevel: parseInt(item.city_level) || 5
    });
    return result;
  } catch (error) {
    console.warn('状态计算失败:', error, item);
    return {
      status: StatusEnum.NORMAL,
      color: '#52c41a',
      icon: '✓',
      text: '正常',
      description: '检查结果正常'
    };
  }
};

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [statsOverview, setStatsOverview] = useState<StatsOverview | null>(null);
  const [warningStats, setWarningStats] = useState<WarningStats | null>(null);
  const [recentBatches, setRecentBatches] = useState<BatchInfo[]>([]);
  const [allReportData, setAllReportData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overview, warnings, batches] = await Promise.all([
        getStatsOverview(),
        getWarningStats(),
        getInspectionBatches(),
      ]);

      setStatsOverview(overview);
      setWarningStats(warnings);
      setRecentBatches(batches.slice(0, 5)); // 只显示最近5个批次

      // 获取所有批次的报告数据
      const allReports: any[] = [];
      for (const batch of batches) {
        try {
          const batchReport = await getBatchReport(batch.batch_id);
          allReports.push(...batchReport.reports);
        } catch (error) {
          console.error(`获取批次 ${batch.batch_id} 报告失败:`, error);
        }
      }
      setAllReportData(allReports);
    } catch (err) {
      console.error('加载仪表板数据失败:', err);
      setError(err instanceof Error ? err.message : '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>正在加载仪表板数据...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  if (!statsOverview) {
    return (
      <Alert
        message="暂无数据"
        description="系统中暂无统计数据"
        type="info"
        showIcon
      />
    );
  }

  // 城市等级分布数据
  const cityLevelData = statsOverview.cities.map((item) => ({
    name: getCityLevelLabel(item.level),
    value: item.count,
    level: item.level,
  }));

  // 检查统计数据
  const inspectionStats = statsOverview.inspections;

  // 使用检查报告数据和StatusCalculationEngine实时计算状态并统计
  const { dangerCount, warningCount, normalCount } = allReportData.length > 0 ? (() => {
    let danger = 0;
    let warning = 0;
    let normal = 0;

    allReportData.forEach(report => {
      // 使用StatusCalculationEngine计算状态
      const { min, max } = parseReferenceRange(report.reference_range);
      let statusResult;
      try {
        statusResult = StatusCalculationEngine.calculate({
          actualValue: report.actual_value,
          referenceMin: min,
          referenceMax: max,
          elementName: report.element_name,
          cityLevel: report.city_level || 5
        });
      } catch (error) {
        statusResult = {
          status: StatusEnum.NORMAL,
          color: '#52c41a',
          icon: '✓',
          text: '正常',
          description: '检查结果正常'
        };
      }

      if (statusResult.status === StatusEnum.DANGEROUS) {
        danger++;
      } else if (statusResult.status === StatusEnum.SLIGHTLY_HIGH) {
        warning++;
      } else {
        normal++;
      }
    });

    return { dangerCount: danger, warningCount: warning, normalCount: normal };
  })() : { dangerCount: 0, warningCount: 0, normalCount: 0 };

  // 预警统计数据
  const warningData = [
    { name: '正常项目', value: normalCount, color: '#52c41a' },
    { name: '预警项目', value: warningCount, color: '#faad14' },
    { name: '危险项目', value: dangerCount, color: '#f5222d' },
  ].filter(item => item.value > 0);

  // 批次表格列定义
  const batchColumns = [
    {
      title: '批次ID',
      dataIndex: 'batch_id',
      key: 'batch_id',
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {text.length > 20 ? `${text.substring(0, 20)}...` : text}
        </Text>
      ),
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: '导入日期',
      dataIndex: 'import_date',
      key: 'import_date',
      render: (date: string) => formatDate(date),
    },
    {
      title: '记录数量',
      dataIndex: 'record_count',
      key: 'record_count',
      render: (count: number) => (
        <Tag color="blue">{count} 条</Tag>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="检查记录总数"
              value={inspectionStats.totalRecords}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="涉及公司数量"
              value={inspectionStats.companiesCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="预警项目"
              value={warningCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="危险项目"
              value={dangerCount}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* 城市等级分布 */}
        <Col xs={24} lg={12}>
          <Card title="城市等级分布" style={{ height: 400 }}>
            {cityLevelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={cityLevelData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={(props: any) => {
                      const total = cityLevelData.reduce((sum, item) => sum + (item.value || 0), 0);
                      const percentage = total > 0 ? ((props.payload?.value || 0) / total * 100).toFixed(1) : '0.0';
                      return `${percentage}%`;
                    }}
                  >
                    {cityLevelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} 个`, '城市数量']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Text type="secondary">暂无城市数据</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* 问题项目地区分布 */}
        <Col xs={24} lg={12}>
          <Card title="问题项目地区分布" style={{ height: 400 }}>
            {(() => {
              // 使用检查报告数据按业务所属创大统计超标问题
              const regionProblemStats = allReportData.length > 0 ? (() => {
                const stats: { [key: string]: { total: number; problems: number } } = {};

                allReportData.forEach(report => {
                  const companyName = report.company_name || '未知公司';

                  // 使用StatusCalculationEngine计算状态
                  const { min, max } = parseReferenceRange(report.reference_range);
                  let statusResult;
                  try {
                    statusResult = StatusCalculationEngine.calculate({
                      actualValue: report.actual_value,
                      referenceMin: min,
                      referenceMax: max,
                      elementName: report.element_name,
                      cityLevel: report.city_level || 5
                    });
                  } catch (error) {
                    statusResult = {
                      status: StatusEnum.NORMAL,
                      color: '#52c41a',
                      icon: '✓',
                      text: '正常',
                      description: '检查结果正常'
                    };
                  }

                  if (!stats[companyName]) {
                    stats[companyName] = { total: 0, problems: 0 };
                  }

                  stats[companyName].total++;

                  // 只统计超标的问题（预警和危险）
                  if (statusResult.status === StatusEnum.SLIGHTLY_HIGH || statusResult.status === StatusEnum.DANGEROUS) {
                    stats[companyName].problems++;
                  }
                });

                // 转换为图表数据，计算比例
                return Object.entries(stats)
                  .map(([company, data], index) => ({
                    name: company,
                    value: data.problems,
                    total: data.total,
                    percentage: data.total > 0 ? ((data.problems / data.total) * 100).toFixed(1) : '0'
                  }))
                  .filter(item => item.value > 0) // 只显示有问题的公司
                  .sort((a, b) => b.value - a.value); // 按问题数量降序排序
              })() : [];

              return regionProblemStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={regionProblemStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={(props: any) => `${props.payload?.percentage || '0.0'}%`}
                    >
                      {regionProblemStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        [`${value} 个问题项目`, '问题数量'],
                        [`${props.payload.percentage}%`, '问题比例'],
                        [`${props.payload.total} 个总项目`, '总数量']
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Text type="secondary">暂无问题项目数据</Text>
                </div>
              );
            })()}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* 最近导入批次 */}
        <Col xs={24} lg={14}>
          <Card title="最近导入批次" style={{ height: 400 }}>
            <Table
              dataSource={recentBatches}
              columns={batchColumns}
              rowKey="batch_id"
              pagination={false}
              size="small"
              style={{ height: 320, overflow: 'auto' }}
            />
          </Card>
        </Col>

        {/* 系统统计信息 */}
        <Col xs={24} lg={10}>
          <Card title="系统统计信息" style={{ height: 400 }}>
            <div style={{ padding: '20px 0' }}>
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Text strong>培训业务类型：</Text>
                  <span style={{ float: 'right' }}>{statsOverview.businesses.total} 种</span>
                </Col>
                <Col span={24}>
                  <Text strong>培训要素类型：</Text>
                  <span style={{ float: 'right' }}>{statsOverview.elements.total} 种</span>
                </Col>
                <Col span={24}>
                  <Text strong>平均费用值：</Text>
                  <span style={{ float: 'right' }}>¥{formatNumber(inspectionStats.avgValue)}</span>
                </Col>
                <Col span={24}>
                  <Text strong>最小费用值：</Text>
                  <span style={{ float: 'right' }}>¥{formatNumber(inspectionStats.minValue)}</span>
                </Col>
                <Col span={24}>
                  <Text strong>最大费用值：</Text>
                  <span style={{ float: 'right' }}>¥{formatNumber(inspectionStats.maxValue)}</span>
                </Col>
                <Col span={24}>
                  <Text strong>业务类型覆盖：</Text>
                  <span style={{ float: 'right' }}>{inspectionStats.businessTypesCount} 种</span>
                </Col>
                <Col span={24}>
                  <Text strong>要素类型覆盖：</Text>
                  <span style={{ float: 'right' }}>{inspectionStats.elementTypesCount} 种</span>
                </Col>
              </Row>
              
              {/* 数据完整度进度条 */}
              <div style={{ marginTop: 30 }}>
                <Text strong>数据完整度</Text>
                <Progress 
                  percent={Math.min(100, (inspectionStats.totalRecords / 100) * 100)} 
                  status="active" 
                  strokeColor="#52c41a"
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;